(() => {
  'use strict';
  if (customElements.get('sissilux-seasonal-effect')) return;
  const shapes = [
    'M31 57C30 52 30 47 28 43L20 46 21 41 12 43 16 37 7 33 12 31 5 25 16 26 14 18 21 22 23 14 28 20 31 4 35 17 39 13 40 23 47 18 47 27 57 24 53 31 58 33 48 37 51 43 41 41 43 47 35 43C33 48 34 54 35 59L33 61Z',
    'M31 57C23 55 19 51 22 46 13 47 11 41 18 37 9 35 11 28 19 28 14 24 16 18 24 20 20 14 24 10 29 14 28 5 35 2 38 9 40 14 37 18 40 20 49 15 53 22 45 27 54 28 55 35 46 37 53 44 47 50 39 47 41 53 37 56 34 57L35 62 32 62Z',
    'M29 57C15 51 13 43 17 34L15 31 19 27 19 23 23 20 24 16 29 13C34 9 39 6 44 3 43 12 46 18 45 24L47 27 43 31 44 35 40 38 40 42 36 45C33 49 32 53 32 57L34 62 31 63Z'
  ];
  const veins = [
    'M32 56Q32 31 32 12M32 31 24 21M32 35 16 29M31 40 19 39M32 28 38 20M32 34 47 28M32 40 44 38M22 30 21 26M42 30 45 24',
    'M32 56Q35 31 34 11M33 47 25 43M34 40 19 32M34 32 24 25M34 23 29 18M34 47 43 42M35 38 46 32M35 29 44 23',
    'M30 57Q28 36 42 8M30 48 21 43M30 40 20 34M32 32 24 27M35 24 28 20M30 46 37 39M31 37 41 30M34 28 42 21M37 20 43 15'
  ];
  const palettes = [
    ['#D8913F','#B45C2B','#73372A'],
    ['#B56239','#8D422E','#623B2B'],
    ['#D2A45B','#A5793B','#76532D'],
    ['#A8B47C','#6E8750','#465E39'],
    ['#B6B77A','#879654','#526D3F']
  ];
  const random = (min, max) => min + Math.random() * (max - min);
  class SeasonalEffect extends HTMLElement {
    connectedCallback() {
      this.dispose();
      if (this.dataset.enabled !== 'true') return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      if (this.motion.matches || (this.mobile.matches && this.dataset.mobile !== 'true')) return;
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.dispose(); }, options);
      this.mobile.addEventListener('change', () => {
        if (this.mobile.matches && this.dataset.mobile !== 'true') this.dispose();
      }, options);
      window.addEventListener('pagehide', () => this.pause(), options);
      window.addEventListener('pageshow', () => this.resume(), options);
      document.addEventListener('visibilitychange', () => document.hidden ? this.pause() : this.resume(), options);
      this.begin = () => { this.startTimer = setTimeout(() => this.start(), 800); };
      if (document.readyState !== 'loading') this.begin();
      else document.addEventListener('DOMContentLoaded', this.begin, {...options, once: true});
    }
    start() {
      if (!this.isConnected || document.hidden || this.motion.matches || this.layer) return;
      this.layer = document.createElement('div');
      this.layer.className = 'sissilux-autumn-layer';
      this.layer.setAttribute('aria-hidden', 'true');
      this.layer.setAttribute('inert', '');
      document.body.appendChild(this.layer);
      this.header = document.querySelector('.section-header');
      this.announcement = document.querySelector('.announcement-bar-section');
      this.syncBounds = () => {
        if (!this.layer) return;
        const bottom = (this.announcement || this.header)?.getBoundingClientRect().bottom || 0;
        this.layer.style.height = `${Math.max(0, Math.min(innerHeight, bottom))}px`;
      };
      this.syncBounds();
      this.headerResize = new ResizeObserver(this.syncBounds);
      if (this.header) this.headerResize.observe(this.header);
      if (this.announcement) this.headerResize.observe(this.announcement);
      window.addEventListener('resize', this.syncBounds, {signal: this.events.signal});
      this.originY = 0;
      this.sequence = 0;
      this.sideSequence = 0;
      this.tick = 0;
      this.spawning = true;
      // Bound clipping updates to at most one per 100ms while the effect is alive.
      this.scrollHandler = () => {
        if (!this.boundsTimer) this.boundsTimer = setTimeout(() => {
          this.boundsTimer = null;
          this.syncBounds();
        }, 100);
      };
      window.addEventListener('scroll', this.scrollHandler, {passive: true, signal: this.events.signal});
      this.spawn();
    }
    spawn() {
      if (!this.spawning || !this.layer) return;
      if (parseFloat(this.layer.style.height) > 0) {
        this.emitLeaf('side');
        this.emitLeaf('center');
      }
      this.tick += 1;
      this.spawnTimer = setTimeout(() => this.spawn(), 1000);
    }
    emitLeaf(region) {
      const mobile = this.mobile.matches;
      const max = mobile ? 21 : 24;
      if (this.layer.childElementCount < max) {
        const leaf = document.createElement('span');
        leaf.className = 'sissilux-autumn-leaf';
        leaf.style.top = `${this.originY}px`;
        const side = region === 'side' ? (this.sideSequence++ % 2 ? 1 : -1) : (Math.random() < 0.5 ? -1 : 1);
        this.sequence += 1;
        leaf.dataset.region = region;
        const start = random(-35,35);
        const life = random(mobile ? 10 : 12, mobile ? 13 : 15) / 1.5;
        const variables = {
          x: `${region === 'center' ? random(35,65) : (side < 0 ? random(3,22) : random(78,94))}%`,
          size: `${random(mobile ? 24 : 34,mobile ? 34 : 48)}px`,
          life: `${life}s`, start: `${start}deg`, end: `${start + side * random(45,85)}deg`,
          drift: `${-side * random(30,mobile ? 45 : 75)}px`,
          wind: `${random(mobile ? 16 : 28,mobile ? 28 : 48)}px`,
          travel: '66.667vh',
          opacity: random(mobile ? 0.68 : 0.72,mobile ? 0.82 : 0.88).toFixed(2)
        };
        Object.entries(variables).forEach(([key,value]) => leaf.style.setProperty(`--leaf-${key}`,value));
        const type = (this.sequence - 1) % shapes.length;
        const palette = palettes[(this.sequence - 1) % palettes.length];
        const gradient = `sx-leaf-${this.sequence}-${Math.random().toString(36).slice(2,7)}`;
        leaf.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" focusable="false" aria-hidden="true">
          <defs><linearGradient id="${gradient}" x1="5%" y1="12%" x2="90%" y2="82%">
            <stop offset="0" stop-color="${palette[0]}"/><stop offset=".44" stop-color="${palette[1]}"/>
            <stop offset=".53" stop-color="${palette[0]}"/><stop offset="1" stop-color="${palette[2]}"/>
          </linearGradient></defs>
          <path fill="url(#${gradient})" stroke="${palette[2]}" stroke-width=".45" stroke-opacity=".55" d="${shapes[type]}"/>
          <path d="${veins[type]}" fill="none" stroke="${palette[2]}" stroke-opacity=".52" stroke-width=".65" stroke-linecap="round"/>
          <path d="M32 55Q32 59 35 63" fill="none" stroke="${palette[2]}" stroke-width="1.3" stroke-linecap="round"/>
        </svg>`;
        const remove = event => {
          if (event.target !== leaf) return;
          leaf.remove();
        };
        leaf.addEventListener('animationend', remove);
        this.layer.appendChild(leaf);
      }
    }
    pause() {
      this.spawning = false;
      clearTimeout(this.spawnTimer);
      if (this.layer) this.layer.replaceChildren();
    }
    resume() {
      if (!this.isConnected || document.hidden || this.motion.matches || (this.mobile.matches && this.dataset.mobile !== 'true')) return;
      if (!this.layer) return this.start();
      this.syncBounds();
      if (!this.spawning) {
        this.spawning = true;
        this.spawn();
      }
    }
    disconnectedCallback() { this.dispose(); }
    dispose() {
      ['startTimer','spawnTimer','boundsTimer'].forEach(key => clearTimeout(this[key]));
      this.spawning = false;
      this.boundsTimer = null;
      if (this.headerResize) this.headerResize.disconnect();
      if (this.events) this.events.abort();
      if (this.layer) this.layer.remove();
      this.layer = null;
    }
  }
  customElements.define('sissilux-seasonal-effect', SeasonalEffect);
})();
